/**
 * Localisation for the tenant app (CLAUDE.md §6).
 *
 * Three rules this module exists to enforce:
 *
 *  1. **No string reaches a screen except through a key.** `en.json` is the
 *     source of truth and is complete; `dv.json` mirrors every key. A locale
 *     test fails the build on drift, because the failure mode of translation is
 *     not a crash, it is a Dhivehi screen with English holes in it that nobody
 *     notices until a Dhivehi speaker opens it.
 *  2. **The locale follows the user, not the browser.** `/auth/me` carries the
 *     signed-in user's `locale` and the profile screen writes it; the browser's
 *     `Accept-Language` is only a first guess for someone who has not signed in
 *     yet. The last choice is mirrored into localStorage so the login screen
 *     itself comes up in the right language on the next visit.
 *  3. **Direction is part of the locale.** Dhivehi is right-to-left, so
 *     switching locale sets `lang` *and* `dir` on `<html>`; §11 asks for a real
 *     RTL switch, and every logical property in the design system keys off that
 *     one attribute.
 *
 * Composition API only (`legacy: false`) — `$t` is available in templates and
 * `useI18n()` in setup, and the Options-API `this.$t` shim stays out of the
 * bundle.
 */

import { applyDirection, type Direction } from '@erpx/ui'
import { createI18n } from 'vue-i18n'

import dv from '@/locales/dv.json'
import en from '@/locales/en.json'

export const SUPPORTED_LOCALES = ['en', 'dv'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'en'

/** Locales written right-to-left. Dhivehi (Thaana) is the only one today. */
const RTL_LOCALES: readonly AppLocale[] = ['dv']

/** Endonyms — a language picker that reads "Dhivehi" to a Dhivehi speaker is
 * a picker they cannot use. Never translated. */
export const LOCALE_NAMES: Readonly<Record<AppLocale, string>> = {
  en: 'English',
  dv: 'ދިވެހި',
}

const STORAGE_KEY = 'erpx.web.locale'

export type MessageSchema = typeof en

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function localeDirection(locale: AppLocale): Direction {
  return RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'
}

function readStoredLocale(): AppLocale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isAppLocale(stored) ? stored : null
  } catch {
    // Private mode or a blocked storage partition; fall through to the default.
    return null
  }
}

function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // A remembered preference is a nicety; losing it costs one extra click.
  }
}

/** The browser's preference, used only before a user has told us theirs. */
function browserLocale(): AppLocale | null {
  if (typeof navigator === 'undefined') return null
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.split('-')[0]
    if (isAppLocale(base)) return base
  }
  return null
}

/** Locale to boot with: last explicit choice, else the browser's, else `en`. */
export function initialLocale(): AppLocale {
  return readStoredLocale() ?? browserLocale() ?? DEFAULT_LOCALE
}

/**
 * Put the locale on the document. `lang` drives hyphenation, spell-check and
 * the accessibility tree; `dir` drives every logical property in the design
 * system. Both, or neither — a `lang="dv"` document laid out left-to-right is
 * worse than an English one.
 */
export function applyDocumentLocale(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', locale)
  }
  applyDirection(localeDirection(locale))
}

const messages: Record<AppLocale, MessageSchema> = { en, dv: dv as MessageSchema }

export const i18n = createI18n<false>({
  legacy: false,
  globalInjection: true,
  locale: initialLocale() as string,
  fallbackLocale: DEFAULT_LOCALE,
  // A key that only exists in `en` is a bug the locale test catches, not
  // something to shout about on every render in development.
  missingWarn: false,
  fallbackWarn: false,
  messages,
})

/** The locale in force right now. */
export function currentLocale(): AppLocale {
  const active = i18n.global.locale.value
  return isAppLocale(active) ? active : DEFAULT_LOCALE
}

/**
 * Switch locale: message catalogue, `<html lang>`, `<html dir>` and the
 * remembered preference, in one call so the four can never disagree.
 */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  persistLocale(locale)
  applyDocumentLocale(locale)
}

/**
 * Adopt the signed-in user's locale. Called after `/auth/me`; a user whose
 * profile says `dv` gets Dhivehi even on a machine that has never asked for it.
 */
export function adoptUserLocale(locale: string | null | undefined): void {
  if (isAppLocale(locale) && locale !== currentLocale()) setLocale(locale)
}

/**
 * Translate outside a component — route titles, notification text built in a
 * store, anything that has no `useI18n()` to reach for.
 */
export const t = i18n.global.t.bind(i18n.global)

/**
 * The catalogue's message for `key`, verbatim — `{placeholder}` and all.
 *
 * `t(key)` called with no interpolation params does not leave a `{count}` in
 * the message alone: vue-i18n still runs its own interpolation pass and
 * replaces every named slot it cannot fill with an empty string. That is
 * invisible for a string with no placeholders, and silent data loss for one
 * that has them — `t('ui.pagination.showing')` (`"Showing {range}"`) resolves
 * to `"Showing "`, not `"Showing {range}"`.
 *
 * `rawT` is for exactly the case that trips this: a handful of `@erpx/ui`
 * components (`EBulkResult`, `EChangesSince`) take a label prop that is a
 * *template*, because the count or date it names is computed inside the
 * component from data the caller does not have yet — so the caller cannot
 * supply the params up front the way `t(key, params)` normally would. `tm()`
 * returns the compiled-but-unevaluated message; for a plain string leaf (every
 * key this build gives it) that is the literal source string, untouched.
 */
export function rawT(key: string): string {
  const raw = i18n.global.tm(key)
  return typeof raw === 'string' ? raw : key
}

applyDocumentLocale(currentLocale())
